import { Alert, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Formik } from "formik";
import { useHandleError } from "Hooks/useHandleError";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { getSecurityQuestions } from "Services/api/crm/crm";
import { SecurityQuestionOption } from "Services/api/crm/interfaces";
import { updateQuestions } from "Services/api/profile/profile";
import FormikForm from "Shared/FormikForm/FormikForm";
import FormikSubmitButton from "Shared/FormikSubmitButton/FormikSubmitButton";
import { Query } from "Shared/Query/Query";
import QuestionsSelect from "Shared/QuestionsSelect/QuestionsSelect";
import { requiredValidation } from "Utils/validations";
import * as Yup from "yup";
import { EditContentProps } from "./interfaces";
import ProfileToken, { checkToken } from "./ProfileToken";

type Step = "edit" | "verification";

export const UserFormSchema = Yup.object({
  question1: requiredValidation,
  answer1: requiredValidation.max(40, "La respuesta no debe exceder 40 caracteres"),
  question2: requiredValidation,
  answer2: requiredValidation.max(40, "La respuesta no debe exceder 40 caracteres"),
});

export function QuestionsChange({ profile, setProfile, closeDialog }: EditContentProps) {
  const handleError = useHandleError();

  const result = useQuery({
    queryKey: [getSecurityQuestions.name],
    queryFn: async () => {
      try {
        const { data: securityQuestions } = await getSecurityQuestions();
        return securityQuestions;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    gcTime: 0,
  });

  const resultToken = useQuery({
    queryKey: [checkToken.name],
    queryFn: async () => {
      try {
        let step: Step = "verification";
        await checkToken(
          () => {
            step = "edit";
          },
          () => {
            step = "verification";
          }
        );

        return step;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    gcTime: 0,
  });

  return (
    <Query
      result={resultToken}
      OnLoading={() => <div>Cargando opciones...</div>}
      onError={() => <ErrorMsg />}
      onSuccess={(initialStep) => (
        <Query
          result={result}
          OnLoading={() => <div>Cargando opciones...</div>}
          onError={() => <ErrorMsg />}
          onSuccess={(value) => (
            <Form initialStep={initialStep} securityQuestions={value} {...{ profile, setProfile, closeDialog }} />
          )}
        />
      )}
    />
  );
}

function ErrorMsg() {
  return (
    <Alert severity="error" variant="outlined">
      Ha ocurrido un error intentando cargar las preguntas de seguridad. Intente nuevamente o comuniquese con un oficial
      de servicio de la cooperativa para cambiar sus preguntas.
    </Alert>
  );
}

interface Payload {
  question1: string;
  answer1: string;
  question2: string;
  answer2: string;
}

const initialValues: Payload = {
  question1: "",
  answer1: "",
  question2: "",
  answer2: "",
};

function Form({
  profile,
  setProfile,
  closeDialog,
  initialStep,
  securityQuestions,
}: EditContentProps & { initialStep: Step; securityQuestions: SecurityQuestionOption[] }) {
  const { enqueueSnackbar } = useSnackbar();
  const [payload, setPayload] = useState<Payload>(initialValues);
  const [step, setStep] = useState<Step>(initialStep);
  const handleError = useHandleError();
  const [error, setError] = useState(false);

  const changeQuestions = useCallback(async (values: Payload) => {
    await checkToken(
      async (token) => {
        const { status, data } = await updateQuestions({
          token: token,
          newSecretQuestions: [
            {
              question: values.question1,
              answer: values.answer1,
            },
            {
              question: values.question2,
              answer: values.answer2,
            },
          ],
        });

        if (status === "success") {
          enqueueSnackbar(data, { variant: "success" });
          setProfile({
            ...profile,
            securityQuestions: [
              {
                question: values.question1,
                answer: "",
              },
              {
                question: values.question2,
                answer: "",
              },
            ],
          });
          closeDialog();
        } else {
          setError(true);
          enqueueSnackbar(data, { variant: "error" });
        }
      },
      () => {
        setPayload(values);
        setStep("verification");
      }
    );
  }, []);

  return step === "edit" ? (
    <Formik
      initialValues={initialValues}
      validationSchema={UserFormSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setError(false);
          await changeQuestions(values);
        } catch (error) {
          setError(true);
          handleError(error);
        }
        setSubmitting(false);
      }}
    >
      <FormikForm width="100%">
        <Stack spacing={2}>
          <Alert severity="info" color="success" variant="outlined">
            Favor de seleccionar las nuevas preguntas y digitar las respectivas respuestas de seguridad.
          </Alert>
          <QuestionsSelect data={securityQuestions} />

          {error && (
            <Alert severity="error" variant="outlined">
              Ha ocurrido un error al intentar guardar las preguntas de seguridad. Intente nuevamente o comuniquese con
              un oficial de servicio de la cooperativa para cambiar sus preguntas de seguridad.
            </Alert>
          )}

          <FormikSubmitButton fullWidth variant="contained">
            Continuar
          </FormikSubmitButton>
        </Stack>
      </FormikForm>
    </Formik>
  ) : (
    <ProfileToken
      secretQuestions={profile.securityQuestions}
      onSubmit={() => {
        setPayload(payload);
        setStep("edit");
      }}
    />
  );
}
